// The Lineage section of the Company detail drawer: former names and the dated
// events that connect two profiles.
//
// This is the human half of the lineage design (18-tenant-identity §8, model b).
// The system's half is resolution: once someone records "Quantum Management LLC
// is a former name of this company", every match point — the upload preflight,
// the staged-change apply — resolves the old spelling to this profile, and a
// re-uploaded pre-merger pricelist stops being a duplicate-in-waiting. Nothing
// here merges anything: both eras keep their own profiles and the rates they
// earned, which is what makes a pre-merger ceiling quotable AS pre-merger.
//
// Writes are registries-gated server-side (any registries authority — decision
// 3); the forms here stay dumb and let the gate speak in its own words.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { formatContractDate } from '../../../lib/contractDate';
import { CorporateEventDialog } from '../../Registries/records/company/CorporateEventDialog';
import { corporateEventSentence } from '../../Registries/records/company/corporateEvents';
import { useCompanyAliases, useCompanyRelationships, useDeleteCompanyAlias, useDeleteCompanyRelationship, useUpsertCompanyAlias } from './hooks';
const ALIAS_TYPES = [
    'former',
    'previous',
    'dba',
    'trading_as'
];
// The sentence a reviewer reads for one edge, direction-aware: the same row is
// "renamed to Sprezzatura" on Quantum's drawer and "formerly Quantum" on
// Sprezzatura's.
function relationshipSentence(rel, hereId, nameOf) {
    const outbound = rel.fromCompanyId === hereId;
    const other = nameOf(outbound ? rel.toCompanyId : rel.fromCompanyId);
    switch(rel.relationshipType){
        case 'rename':
            return outbound ? `Renamed to ${other}` : `Formerly ${other}`;
        case 'merger':
            return outbound ? `Merged into ${other}` : `Absorbed ${other} by merger`;
        // DAT-25: projected from SBA's list (mentor → protégé). Display only.
        case 'mentor_protege':
            return outbound ? `Mentor–protégé (SBA): mentor to ${other}` : `Mentor–protégé (SBA): protégé of ${other}`;
        default:
            // REG-62: the record page's wording for every other type.
            return corporateEventSentence(rel.relationshipType, outbound, other);
    }
}
// C1.1-04: unknown event dates stay unknown — never inferred, never blank.
function relationshipDatesLabel(rel) {
    const start = rel.effectiveStart ? formatContractDate(rel.effectiveStart) : 'Date unknown';
    const end = rel.effectiveEnd ? formatContractDate(rel.effectiveEnd) : 'Date unknown';
    return `Effective ${start} – ${end}`;
}
export function CompanyLineageSection({ companyProfileId, companies }) {
    const aliases = useCompanyAliases(companyProfileId);
    const relationships = useCompanyRelationships(companyProfileId);
    const upsertAlias = useUpsertCompanyAlias();
    const deleteAlias = useDeleteCompanyAlias();
    const deleteRel = useDeleteCompanyRelationship();
    const [addingAlias, setAddingAlias] = useState(false);
    const [aliasName, setAliasName] = useState('');
    const [aliasType, setAliasType] = useState('former');
    // REG-62: events are recorded in the same dialog as the company record page.
    const [addingRel, setAddingRel] = useState(false);
    const nameOf = (id)=>companies.find((c)=>c.id === id)?.name ?? 'another company';
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.deliveryRegistries.companyLineageSection,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                children: "Lineage"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Former names are this same company. Corporate events link separate companies — each keeps its own record, contacts and rates; none is treated as the same identity."
            }),
            (aliases.data ?? []).map((a)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between rounded-md border border-border/60 p-2 text-[12.5px]",
                    "data-testid": Testids.deliveryRegistries.companyAliasRow,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: a.aliasName
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    "data-testid": Testids.deliveryRegistries.companyLineageIdentityBadge,
                                    variant: "success",
                                    children: "Same company"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsxs(SpecraBadge, {
                                    variant: "neutral",
                                    children: [
                                        a.aliasType.replace('_', ' '),
                                        " name of this company"
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.deliveryRegistries.companyAliasDelete,
                            onClick: ()=>deleteAlias.mutate(a.id),
                            size: "sm",
                            variant: "ghost",
                            children: "Remove"
                        })
                    ]
                }, a.id)),
            (relationships.data ?? []).map((r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between rounded-md border border-border/60 p-2 text-[12.5px]",
                    "data-testid": Testids.deliveryRegistries.companyRelationshipRow,
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            children: [
                                relationshipSentence(r, companyProfileId, nameOf),
                                ' ',
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    "data-testid": Testids.deliveryRegistries.companyLineageIdentityBadge,
                                    variant: "neutral",
                                    children: "Separate company"
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-muted-foreground",
                                    children: relationshipDatesLabel(r)
                                })
                            ]
                        }),
                        r.source === 'sba_mp_xlsx' ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.deliveryRegistries.companyRelationshipDelete,
                            onClick: ()=>deleteRel.mutate(r.id),
                            size: "sm",
                            variant: "ghost",
                            children: "Remove"
                        })
                    ]
                }, r.id)),
            (aliases.data ?? []).length === 0 && (relationships.data ?? []).length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "No lineage recorded. A former name added here resolves to this company on every future upload — a renamed contractor's old pricelists stop looking like a different company."
            }) : null,
            addingAlias ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border border-border/60 p-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "data-testid": Testids.deliveryRegistries.companyAliasNameInput,
                        onChange: (e)=>setAliasName(e.target.value),
                        placeholder: "Former name exactly as documents write it",
                        value: aliasName
                    }),
                    /*#__PURE__*/ _jsx(SpecraSelect, {
                        "data-testid": Testids.deliveryRegistries.companyAliasTypeSelect,
                        onChange: (e)=>setAliasType(e.target.value),
                        value: aliasType,
                        children: ALIAS_TYPES.map((t)=>/*#__PURE__*/ _jsx("option", {
                                value: t,
                                children: t.replace('_', ' ')
                            }, t))
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                onClick: ()=>setAddingAlias(false),
                                size: "sm",
                                variant: "outline",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.deliveryRegistries.companyAliasSubmit,
                                disabled: !aliasName.trim() || upsertAlias.isPending,
                                onClick: ()=>upsertAlias.mutate({
                                        aliasName: aliasName.trim(),
                                        aliasType,
                                        companyProfileId
                                    }, {
                                        onSuccess: ()=>{
                                            setAliasName('');
                                            setAddingAlias(false);
                                        }
                                    }),
                                size: "sm",
                                children: "Record former name"
                            })
                        ]
                    })
                ]
            }) : null,
            addingRel ? /*#__PURE__*/ _jsx(CorporateEventDialog, {
                open: true,
                onOpenChange: setAddingRel,
                companyId: companyProfileId,
                companyName: nameOf(companyProfileId),
                mode: {
                    kind: 'add'
                }
            }) : null,
            !addingAlias ? /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.deliveryRegistries.companyAliasAdd,
                        onClick: ()=>setAddingAlias(true),
                        size: "sm",
                        variant: "outline",
                        children: "Add former name"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.deliveryRegistries.companyRelationshipAdd,
                        onClick: ()=>setAddingRel(true),
                        size: "sm",
                        variant: "outline",
                        children: "Add corporate event"
                    })
                ]
            }) : null
        ]
    });
}
